/* eslint-disable @typescript-eslint/no-explicit-any */
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Eye } from "lucide-react";
import { useCallback, useEffect, useState, useMemo } from "react";

import { cn } from "@/lib/utils";
import { AgGridReact } from "ag-grid-react";
import { Checkbox } from "../ui/checkbox";

interface ColumnVisibilityProps {
  gridRef: React.RefObject<AgGridReact<any> | null>;
  className?: string;
  trigger?: React.ReactNode;
  storageKey?: string;
  triggerClassName?: string;
}

interface ColumnState {
  field: string;
  headerName?: string;
  visible: boolean;
  order: number;
  width?: number;
}

export function ColumnVisibility({
  gridRef,
  className,
  trigger,
  storageKey = "gridColumnState",
  triggerClassName,
}: ColumnVisibilityProps) {
  const [columnStates, setColumnStates] = useState<ColumnState[]>([]);
  const [isOpen, setIsOpen] = useState(false);

  // Initialize column states when dropdown opens or grid changes
  const initializeColumns = useCallback(() => {
    if (!gridRef.current?.api) return;

    // Fall back to grid state
    const columns = gridRef.current.api.getColumns();
    const initialStates = columns?.map((col, index) => ({
      field: col.getColId(),
      headerName: col?.getColDef()?.headerName ?? col.getColId(),
      visible: col.isVisible(),
      order: index,
      width: col.getActualWidth(),
    }));

    setColumnStates(initialStates || []);
  }, [gridRef]);

  // Load saved column state from localStorage
  useEffect(() => {
    if (!storageKey || !gridRef.current?.api) return;

    // Only run this effect once when the component mounts
    const savedState = localStorage.getItem(storageKey);
    if (savedState) {
      try {
        const parsed = JSON.parse(savedState);
        setColumnStates(parsed);
      } catch (e) {
        console.warn("Failed to parse saved column state:", e);
        // If parsing fails, initialize from the grid
        initializeColumns();
      }
    } else {
      // No saved state, initialize from grid
      initializeColumns();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]); // Only depend on storageKey to prevent loops

  // Apply column states to the grid
  const applyColumnStates = useCallback(() => {
    if (!gridRef.current?.api || !columnStates.length) return;

    const api = gridRef.current.api;

    // First, apply visibility changes in batch
    const visibleColumns = columnStates.filter((col) => col.visible);
    const hiddenColumns = columnStates.filter((col) => !col.visible);

    if (visibleColumns.length) {
      api.setColumnsVisible(
        visibleColumns.map((col) => col.field),
        true
      );
    }

    if (hiddenColumns.length) {
      api.setColumnsVisible(
        hiddenColumns.map((col) => col.field),
        false
      );
    }

    // Force a redraw to ensure visibility changes are applied
    api.redrawRows();

    // Save to localStorage
    if (storageKey) {
      localStorage.setItem(storageKey, JSON.stringify(columnStates));
    }
  }, [columnStates, gridRef, storageKey]);

  // Initialize when grid is ready
  useEffect(() => {
    if (!gridRef.current?.api) return;

    // Add event listeners for events that might affect column visibility
    const onFirstDataRendered = () => {
      // Apply column states only on first render
      applyColumnStates();
    };

    const api = gridRef.current.api;
    api.addEventListener("firstDataRendered", onFirstDataRendered);

    return () => {
      if (gridRef.current?.api) {
        const api = gridRef.current.api;
        api.removeEventListener("firstDataRendered", onFirstDataRendered);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridRef.current?.api]); // Only depend on grid api instance

  // Apply changes only when manually changed through the UI
  const handleVisibilityChange = useCallback(
    (field: string) => {
      // Get the current state of the column before updating
      const columnToToggle = columnStates.find((col) => col.field === field);
      if (!columnToToggle) return;

      const newVisibility = !columnToToggle.visible;

      // Update our internal state
      setColumnStates((prev) =>
        prev.map((col) =>
          col.field === field ? { ...col, visible: newVisibility } : col
        )
      );

      // Apply to the grid directly
      if (gridRef.current?.api) {
        gridRef.current.api.setColumnsVisible([field], newVisibility);

        // Save to localStorage
        if (storageKey) {
          const updatedStates = columnStates.map((col) =>
            col.field === field ? { ...col, visible: newVisibility } : col
          );
          localStorage.setItem(storageKey, JSON.stringify(updatedStates));
        }
      }
    },
    [columnStates, gridRef, storageKey]
  );

  const handleDropdownOpenChange = useCallback(
    (open: boolean) => {
      if (open && gridRef.current?.api) {
        // Only initialize columns when opening the dropdown
        initializeColumns();
      }
      setIsOpen(open);
    },
    [gridRef, initializeColumns]
  );

  // Memoize the dropdown content to prevent unnecessary re-renders
  const dropdownContent = useMemo(() => {
    return columnStates.map((column) => (
      <div
        key={column.field}
        className={cn(
          "flex items-center px-2 py-1.5 hover:bg-accent rounded-md"
        )}
      >
        <div className="flex items-center gap-2 w-full">
          <Checkbox
            checked={column.visible}
            onCheckedChange={() => handleVisibilityChange(column.field)}
            className=""
          />
          <span className="text-sm text-foreground">{column.headerName}</span>
        </div>
      </div>
    ));
  }, [columnStates, handleVisibilityChange]);

  return (
    <DropdownMenu open={isOpen} onOpenChange={handleDropdownOpenChange}>
      <DropdownMenuTrigger asChild>
        {trigger || (
          <Button variant="outline" size="sm" className={cn(triggerClassName)}>
            <Eye className="h-4 w-4" />
            <span>Hide/Unhide</span>
          </Button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className={cn(
          "max-w-[300px] overflow-y-auto max-h-[300px] py-1",
          className
        )}
      >
        {dropdownContent}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
